import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { style: {"text-align":"end","margin-bottom":"1rem"} }

import {computed, onMounted, ref, defineAsyncComponent, watch} from "vue";
import {fragmentParams, getConfiguredUrl} from "@/fragment-params";
import {mdiConsoleLine, mdiKey, mdiPlus, mdiAutoFix, mdiGithub, mdiClose, mdiFire, mdiCollapseAll, mdiExpandAll, mdiMinus, mdiEyeOff, mdiEye, mdiAlertCircle} from "@mdi/js";
import {AuthKeySet, storeAuthKeySet} from "@/authKeySets";
import {getServerHostCommand} from "@/getServerHostCommand";
import CopyToClipboardButton from "@/components/CopyToClipboardButton.vue";
import {createRequiredRules} from "@/createRequiredRules";
import DialogsForGlobal from "@/components/Globals/Globals.vue";
import {showSnackbar} from "@/components/Globals/snackbar/global-snackbar";
import {supportsRequestStreamsPromise} from "@/supportsRequestStreamsPromise";

export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const PipingSsh = defineAsyncComponent(() => import("@/components/PipingSsh.vue"));
const KeyManager = defineAsyncComponent(() => import("@/components/KeyManager.vue"));
const KeysEditor = defineAsyncComponent(() => import("@/components/KeysEditor.vue"));
const KeyGenerator = defineAsyncComponent(() => import("@/components/KeyGenerator.vue"));


const supportsRequestStreams = ref(true /* There are many Chromium-based browser users for now */);
supportsRequestStreamsPromise.then(supports => supportsRequestStreams.value = supports);

const pipingServerUrl = ref<string>(fragmentParams.pipingServerUrl() ?? "https://ppng.io");
const pipingServerUrls = ref<string[]>([
  "https://ppng.io",
  "https://piping.nwtgck.repl.co",
]);
const editingPipingServerHeaders = ref<Array<[string, string]>>(fragmentParams.pipingServerHeaders() ?? []);
const pipingServerHeaders = computed<Array<[string, string]>>(() => {
  return editingPipingServerHeaders.value.filter(([name,value]) => name !== "");
});
const csPath = ref<string>(fragmentParams.csPath() ?? randomString(4));
const scPath = ref<string>(fragmentParams.scPath() ?? randomString(4));
const username = ref<string>(fragmentParams.sshUsername() ?? "");
const sshServerPortForCommandHint = ref<string>(fragmentParams.sshServerPortForHint() ?? "22");

const editingSshPassword = ref<string>(fragmentParams.sshPassword() ?? "");
const showsSshPassword = ref(false);
const emptySshPassword = ref<boolean>(fragmentParams.sshPassword() === "");
const sshPassword = computed<string | undefined>(() => {
  if (editingSshPassword.value === "" && !emptySshPassword.value) {
    return undefined;
  }
  return editingSshPassword.value;
});

const includesSshPasswordInFragmentParams = ref<boolean>(fragmentParams.sshPassword() !== undefined);
const autoConnectForFragmentParams = ref<boolean>(fragmentParams.autoConnect() ?? false);

const formValid = ref(false);
const connecting = ref<boolean>(false);

function connect() {
  connecting.value = true;
}

const showsMoreOptions = ref(false);
const keyManagerDialog = ref(false);
const newKeyDialog = ref(false);
const generateKeyDialog = ref(false);

onMounted(() => {
  if (fragmentParams.autoConnect()) {
    connect();
  }
  window.addEventListener('load', () => {
    preloadForUserExperience();
  });
});

function preloadForUserExperience() {
  import("xterm");
  import("xterm-addon-fit");
  import("@/components/PipingSsh.vue");
  import("clipboard-copy");
  import("@/components/KeyManager.vue");
  import("@/components/KeysEditor.vue");
  import("@/components/KeyGenerator.vue");
}

const serverHostCommandEditing = ref<string>("");
const serverHostCommand = computed<string>(() => {
  return getServerHostCommand({
    // NOTE: v-combobox makes pipingServerUrl null
    pipingServerUrl: pipingServerUrl.value ?? "",
    pipingServerHeaders: pipingServerHeaders.value,
    csPath: csPath.value,
    scPath: scPath.value,
    sshServerPort: sshServerPortForCommandHint.value,
  });
});
watch(serverHostCommand, () => {
  serverHostCommandEditing.value = serverHostCommand.value;
}, {
  immediate: true,
});

async function saveAuthKeySet(authKeySet: AuthKeySet) {
  newKeyDialog.value = false;
  generateKeyDialog.value = false;
  await storeAuthKeySet(authKeySet);
}

function randomString(len){
  const nonConfusingChars = ["a", "b", "c", "d", "e", "f", "h", "i", "j", "k", "m", "n", "p", "r", "s", "t", "u", "v", "w", "x", "y", "z", "2", "3", "4", "5", "6", "7", "8"];
  const randomArr = window.crypto.getRandomValues(new Uint32Array(len));
  return Array.from(randomArr).map(n => nonConfusingChars[n % nonConfusingChars.length]).join('');
}

function setConfiguredUrl() {
  location.href = getConfiguredUrl({
    pipingServerUrl: pipingServerUrl.value,
    pipingServerHeaders: pipingServerHeaders.value,
    csPath: csPath.value,
    scPath: scPath.value,
    sshUsername: username.value,
    sshPassword: includesSshPasswordInFragmentParams.value ? sshPassword.value : undefined,
    sshServerPortForHint: sshServerPortForCommandHint.value,
    autoConnect: autoConnectForFragmentParams.value,
  });
  showSnackbar({
    message: "URL updated",
  });
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_app_bar = _resolveComponent("v-app-bar")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_combobox = _resolveComponent("v-combobox")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_main = _resolveComponent("v-main")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_app = _resolveComponent("v-app")!

  return (_openBlock(), _createBlock(_component_v_app, { theme: "dark" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_app_bar, { flat: "" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_container, { class: "d-flex align-center" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_avatar, {
                class: "me-4 ms-4",
                color: "grey-darken-3",
                size: "32"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, { icon: _unref(mdiConsoleLine) }, null, 8, ["icon"])
                ]),
                _: 1
              }),
              _cache[28] || (_cache[28] = _createElementVNode("a", {
                href: "",
                class: "me-4 font-weight-bold",
                style: {"color":"inherit","text-decoration":"none"}
              }, " Piping SSH ", -1)),
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                onClick: _cache[0] || (_cache[0] = ($event: any) => (keyManagerDialog.value = !keyManagerDialog.value)),
                variant: "text",
                "prepend-icon": _unref(mdiKey)
              }, {
                default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                  _createTextVNode(" Manage keys ", -1)
                ]))]),
                _: 1
              }, 8, ["prepend-icon"]),
              _createVNode(_component_v_btn, {
                icon: _unref(mdiGithub),
                href: "https://github.com/nwtgck/piping-ssh-web",
                target: "_blank"
              }, null, 8, ["icon"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_main, null, {
        default: _withCtx(() => [
          (!connecting.value)
            ? (_openBlock(), _createBlock(_component_v_container, { key: 0 }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, null, {
                        default: _withCtx(() => [
                          (!supportsRequestStreams.value)
                            ? (_openBlock(), _createBlock(_component_v_sheet, { key: 0 }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_alert, {
                                    color: "warning",
                                    icon: _unref(mdiAlertCircle),
                                    variant: "outlined",
                                    prominent: "",
                                    border: "top",
                                    style: {"margin-bottom":"2rem"}
                                  }, {
                                    text: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                                      _createTextVNode(" Sorry, this browser is not supported.", -1),
                                      _createElementVNode("br", null, null, -1),
                                      _createTextVNode(" Use Google Chrome 105 or higher.", -1),
                                      _createElementVNode("br", null, null, -1),
                                      _createTextVNode(" You can also use Microsoft Edge or other Chromium-based browsers. ", -1)
                                    ]))]),
                                    _: 1
                                  }, 8, ["icon"])
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          _createVNode(_component_v_sheet, {
                            "min-height": "70vh",
                            rounded: "lg",
                            style: {"padding":"1rem"}
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_form, {
                                onSubmit: _withModifiers(connect, ["prevent"]),
                                modelValue: formValid.value,
                                "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((formValid).value = $event)),
                                disabled: !supportsRequestStreams.value
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_combobox, {
                                    label: "Piping Server",
                                    modelValue: pipingServerUrl.value,
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((pipingServerUrl).value = $event)),
                                    items: pipingServerUrls.value,
                                    required: "",
                                    variant: "solo-filled",
                                    rules: _unref(createRequiredRules)('Piping Server')
                                  }, null, 8, ["modelValue", "items", "rules"]),
                                  _createVNode(_component_v_row, null, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_col, null, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_text_field, {
                                            label: "client-server path",
                                            modelValue: csPath.value,
                                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((csPath).value = $event)),
                                            required: "",
                                            variant: "solo-filled",
                                            rules: _unref(createRequiredRules)('client-server path')
                                          }, null, 8, ["modelValue", "rules"])
                                        ]),
                                        _: 1
                                      }),
                                      _createVNode(_component_v_col, null, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_text_field, {
                                            label: "server-client path",
                                            modelValue: scPath.value,
                                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((scPath).value = $event)),
                                            required: "",
                                            variant: "solo-filled",
                                            rules: _unref(createRequiredRules)('server-client path')
                                          }, null, 8, ["modelValue", "rules"])
                                        ]),
                                        _: 1
                                      })
                                    ]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_text_field, {
                                    label: "user name",
                                    modelValue: username.value,
                                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((username).value = $event)),
                                    required: "",
                                    variant: "solo-filled",
                                    rules: _unref(createRequiredRules)('user name')
                                  }, null, 8, ["modelValue", "rules"]),
                                  (showsMoreOptions.value)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(editingPipingServerHeaders.value, (header, idx) => {
                                          return (_openBlock(), _createBlock(_component_v_row, null, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_col, null, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_v_text_field, {
                                                    modelValue: header[0],
                                                    "onUpdate:modelValue": ($event: any) => ((header[0]) = $event),
                                                    label: `HTTP header name ${idx + 1}`,
                                                    variant: "solo-filled"
                                                  }, null, 8, ["modelValue", "onUpdate:modelValue", "label"])
                                                ]),
                                                _: 2
                                              }, 1024),
                                              _createVNode(_component_v_col, null, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_v_text_field, {
                                                    modelValue: header[1],
                                                    "onUpdate:modelValue": ($event: any) => ((header[1]) = $event),
                                                    label: `HTTP header value ${idx + 1}`,
                                                    variant: "solo-filled"
                                                  }, null, 8, ["modelValue", "onUpdate:modelValue", "label"])
                                                ]),
                                                _: 2
                                              }, 1024),
                                              _createVNode(_component_v_col, null, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_v_btn, {
                                                    icon: _unref(mdiMinus),
                                                    onClick: ($event: any) => (editingPipingServerHeaders.value.splice(idx, 1)),
                                                    variant: "text"
                                                  }, null, 8, ["icon", "onClick"])
                                                ]),
                                                _: 2
                                              }, 1024)
                                            ]),
                                            _: 2
                                          }, 1024))
                                        }), 256)),
                                        _createVNode(_component_v_btn, {
                                          onClick: _cache[5] || (_cache[5] = ($event: any) => (editingPipingServerHeaders.value.push(['', '']))),
                                          "prepend-icon": _unref(mdiPlus),
                                          variant: "outlined",
                                          style: {"margin-bottom":"1rem","text-transform":"none"}
                                        }, {
                                          default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                                            _createTextVNode(" Add header ", -1)
                                          ]))]),
                                          _: 1
                                        }, 8, ["prepend-icon"]),
                                        _createVNode(_component_v_text_field, {
                                          modelValue: sshServerPortForCommandHint.value,
                                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((sshServerPortForCommandHint).value = $event)),
                                          label: "SSH server port for command",
                                          variant: "solo-filled"
                                        }, null, 8, ["modelValue"]),
                                        _createVNode(_component_v_text_field, {
                                          modelValue: editingSshPassword.value,
                                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((editingSshPassword).value = $event)),
                                          label: "SSH password",
                                          type: showsSshPassword.value ? 'text' : 'password',
                                          variant: "solo-filled"
                                        }, {
                                          "append-inner": _withCtx(() => [
                                            _createVNode(_component_v_btn, {
                                              onClick: _cache[7] || (_cache[7] = ($event: any) => (showsSshPassword.value = !showsSshPassword.value)),
                                              icon: showsSshPassword.value ? _unref(mdiEyeOff) : _unref(mdiEye),
                                              variant: "text"
                                            }, null, 8, ["icon"])
                                          ]),
                                          _: 1
                                        }, 8, ["modelValue", "type"]),
                                        _createVNode(_component_v_checkbox, {
                                          modelValue: emptySshPassword.value,
                                          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((emptySshPassword).value = $event)),
                                          label: "Empty SSH password"
                                        }, null, 8, ["modelValue"]),
                                        _createVNode(_component_v_checkbox, {
                                          modelValue: includesSshPasswordInFragmentParams.value,
                                          "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((includesSshPasswordInFragmentParams).value = $event)),
                                          label: "Include SSH password in configured URL"
                                        }, null, 8, ["modelValue"]),
                                        _createVNode(_component_v_checkbox, {
                                          modelValue: autoConnectForFragmentParams.value,
                                          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((autoConnectForFragmentParams).value = $event)),
                                          label: "Auto connect for configured URL"
                                        }, null, 8, ["modelValue"])
                                      ], 64))
                                    : _createCommentVNode("", true),
                                  _createVNode(_component_v_btn, {
                                    type: "submit",
                                    disabled: !formValid.value || !supportsRequestStreams.value,
                                    block: "",
                                    class: "mt-8",
                                    color: "secondary"
                                  }, {
                                    default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                                      _createTextVNode(" Connect ", -1)
                                    ]))]),
                                    _: 1
                                  }, 8, ["disabled"]),
                                  _createVNode(_component_v_btn, {
                                    onClick: _cache[12] || (_cache[12] = ($event: any) => (showsMoreOptions.value = !showsMoreOptions.value)),
                                    "prepend-icon": showsMoreOptions.value ? _unref(mdiCollapseAll) : _unref(mdiExpandAll),
                                    variant: "text",
                                    style: {"margin-top":"1.2rem","text-transform":"none"}
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(showsMoreOptions.value ? "Hide options" : "More options"), 1)
                                    ]),
                                    _: 1
                                  }, 8, ["prepend-icon"])
                                ]),
                                _: 1
                              }, 8, ["modelValue", "disabled"]),
                              _createVNode(_component_v_spacer, { style: {"margin-top":"4rem"} }),
                              _createVNode(_component_v_textarea, {
                                label: "server-host command",
                                modelValue: serverHostCommandEditing.value,
                                "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((serverHostCommandEditing).value = $event)),
                                variant: "outlined",
                                rows: "2",
                                class: "text-grey"
                              }, {
                                "append-inner": _withCtx(() => [
                                  _createVNode(CopyToClipboardButton, { text: serverHostCommandEditing.value }, null, 8, ["text"])
                                ]),
                                _: 1
                              }, 8, ["modelValue"]),
                              _createVNode(_component_v_btn, {
                                color: "grey",
                                onClick: _cache[15] || (_cache[15] = ($event: any) => (setConfiguredUrl())),
                                "prepend-icon": _unref(mdiFire),
                                variant: "outlined",
                                style: {"text-transform":"none"}
                              }, {
                                default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                                  _createTextVNode(" Set configured URL ", -1)
                                ]))]),
                                _: 1
                              }, 8, ["prepend-icon"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (connecting.value)
            ? (_openBlock(), _createBlock(_unref(PipingSsh), {
                key: 1,
                "piping-server-url": pipingServerUrl.value,
                "piping-server-headers": pipingServerHeaders.value,
                "default-ssh-password": sshPassword.value,
                "cs-path": csPath.value,
                "sc-path": scPath.value,
                username: username.value,
                onEnd: _cache[16] || (_cache[16] = ($event: any) => (connecting.value = false))
              }, null, 8, ["piping-server-url", "piping-server-headers", "default-ssh-password", "cs-path", "sc-path", "username"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: keyManagerDialog.value,
        "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((keyManagerDialog).value = $event)),
        scrollable: "",
        width: "90vw"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "d-flex" }, {
                default: _withCtx(() => [
                  _cache[33] || (_cache[33] = _createElementVNode("div", { class: "ma-2" }, "Keys", -1)),
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    onClick: _cache[17] || (_cache[17] = ($event: any) => (keyManagerDialog.value = false)),
                    icon: _unref(mdiClose),
                    variant: "text"
                  }, null, 8, ["icon"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_divider),
              _createVNode(_component_v_card_text, { style: {"min-height":"70vh"} }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_1, [
                    _createVNode(_component_v_btn, {
                      onClick: _cache[18] || (_cache[18] = ($event: any) => (newKeyDialog.value = !newKeyDialog.value)),
                      "prepend-icon": _unref(mdiPlus),
                      color: "secondary",
                      style: {"margin-right":"1rem"}
                    }, {
                      default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                        _createTextVNode(" New ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["prepend-icon"]),
                    _createVNode(_component_v_btn, {
                      onClick: _cache[19] || (_cache[19] = ($event: any) => (generateKeyDialog.value = !generateKeyDialog.value)),
                      "prepend-icon": _unref(mdiAutoFix),
                      color: "secondary"
                    }, {
                      default: _withCtx(() => [...(_cache[35] || (_cache[35] = [
                        _createTextVNode(" Generate ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["prepend-icon"])
                  ]),
                  _createVNode(_unref(KeyManager))
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: newKeyDialog.value,
        "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((newKeyDialog).value = $event)),
        width: "80vw"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "d-flex" }, {
                default: _withCtx(() => [
                  _cache[36] || (_cache[36] = _createElementVNode("div", { class: "ma-2" }, "New key", -1)),
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    onClick: _cache[21] || (_cache[21] = ($event: any) => (newKeyDialog.value = false)),
                    icon: _unref(mdiClose),
                    variant: "text"
                  }, null, 8, ["icon"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_divider),
              _createVNode(_component_v_card_text, { style: {"min-height":"70vh"} }, {
                default: _withCtx(() => [
                  _createVNode(_unref(KeysEditor), {
                    onSave: _cache[22] || (_cache[22] = ($event: any) => (saveAuthKeySet($event)))
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: generateKeyDialog.value,
        "onUpdate:modelValue": _cache[26] || (_cache[26] = ($event: any) => ((generateKeyDialog).value = $event)),
        width: "80vw"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "d-flex" }, {
                default: _withCtx(() => [
                  _cache[37] || (_cache[37] = _createElementVNode("div", { class: "ma-2" }, "Key generator", -1)),
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    onClick: _cache[24] || (_cache[24] = ($event: any) => (generateKeyDialog.value = false)),
                    icon: _unref(mdiClose),
                    variant: "text"
                  }, null, 8, ["icon"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_divider),
              _createVNode(_component_v_card_text, { style: {"min-height":"70vh"} }, {
                default: _withCtx(() => [
                  _createVNode(_unref(KeyGenerator), {
                    onSave: _cache[25] || (_cache[25] = ($event: any) => (saveAuthKeySet($event)))
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(DialogsForGlobal)
    ]),
    _: 1
  }))
}
}

})